/**
 * Registrierung: neue Firma gründen (→ man wird Leitung/Manager) oder mit
 * Einladungs-Code einer bestehenden Firma beitreten (→ Mitarbeiter).
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

export default function RegisterPage() {
  const register = useAuth((s) => s.register);
  const navigate = useNavigate();

  const [mode, setMode] = useState<'create' | 'join'>('create');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    setBusy(true);
    try {
      await register({
        name: name.trim(),
        email: email.trim(),
        password,
        mode,
        companyName: mode === 'create' ? companyName.trim() : undefined,
        joinCode: mode === 'join' ? joinCode.trim().toUpperCase() : undefined,
      });
      hapticSuccess();
      navigate('/', { replace: true });
    } catch (err) {
      hapticError();
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? `Verbindungsfehler: ${err.message}`
            : 'Registrierung fehlgeschlagen',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-sm mx-auto mt-8 sm:mt-12">
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-5">
          <img src="/icon.svg" alt="" className="w-8 h-8 rounded" aria-hidden />
          <h1 className="text-lg font-semibold">Registrieren</h1>
        </div>

        {/* Modus */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            type="button"
            onClick={() => setMode('create')}
            className={`rounded py-2 text-sm !min-h-0 border transition ${
              mode === 'create'
                ? 'bg-brand text-white border-brand'
                : 'bg-surface-3 text-slate-200 border-border hover:bg-slate-600'
            }`}
          >
            Neue Firma gründen
          </button>
          <button
            type="button"
            onClick={() => setMode('join')}
            className={`rounded py-2 text-sm !min-h-0 border transition ${
              mode === 'join'
                ? 'bg-brand text-white border-brand'
                : 'bg-surface-3 text-slate-200 border-border hover:bg-slate-600'
            }`}
          >
            Firma beitreten
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Name</span>
            <input required value={name} onChange={(e) => setName(e.target.value)} className="input" />
          </label>
          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">E-Mail</span>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              autoComplete="email"
            />
          </label>
          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Passwort</span>
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              autoComplete="new-password"
              placeholder="mind. 8 Zeichen"
            />
          </label>

          {mode === 'create' ? (
            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Firmenname</span>
              <input
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="input"
                placeholder="z. B. Bäckerei Müller"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Du wirst automatisch Leitung dieser Firma und bekommst einen Einladungs-Code.
              </p>
            </label>
          ) : (
            <label className="block">
              <span className="block text-sm text-slate-300 mb-1">Beitritts-Code</span>
              <input
                required
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="input tracking-widest uppercase"
                placeholder="z. B. AB3D7KP2"
                autoCapitalize="characters"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Den Code bekommst du von der Leitung deiner Firma.
              </p>
            </label>
          )}

          {error && <p className="text-sm text-red-400">{error}</p>}

          <button type="submit" disabled={busy} className="btn w-full">
            {busy ? 'Wird angelegt…' : mode === 'create' ? 'Firma gründen & starten' : 'Beitreten'}
          </button>
        </form>

        <p className="text-xs text-slate-400 text-center mt-4">
          Schon registriert?{' '}
          <Link to="/login" className="text-brand-light hover:text-brand !min-h-0 underline">
            Zum Login
          </Link>
        </p>
      </div>
    </div>
  );
}
